import { useEffect, useMemo, useRef, useState } from 'react';
import { africanMarkets } from '@/mocks/markets';
import { ALL_AFRICAN_MARKETS } from '@/utils/markets';

interface MarketSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Renders a slim single-line control (filters) instead of a chip field (forms). */
  compact?: boolean;
  className?: string;
}

function CheckBox({ checked }: { checked: boolean }) {
  return (
    <span
      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border text-[11px] ${
        checked
          ? 'border-primary-500 bg-primary-500 text-background-50'
          : 'border-background-300 text-transparent'
      }`}
    >
      <i className="ri-check-line" />
    </span>
  );
}

export default function MarketSelect({
  value,
  onChange,
  id,
  placeholder = 'Select markets',
  disabled = false,
  compact = false,
  className = '',
}: MarketSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const allSelected = value.length === africanMarkets.length;

  const results = useMemo(() => {
    const text = query.trim().toLowerCase();
    if (!text) return africanMarkets;
    return africanMarkets.filter((market) => market.name.toLowerCase().includes(text));
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const toggle = (name: string) => {
    onChange(value.includes(name) ? value.filter((item) => item !== name) : [...value, name]);
    setQuery('');
  };

  const toggleAll = () => {
    onChange(allSelected ? [] : africanMarkets.map((market) => market.name));
    setQuery('');
  };

  const summary =
    value.length === 0
      ? placeholder
      : allSelected
        ? ALL_AFRICAN_MARKETS
        : value.length === 1
          ? value[0]
          : `${value.length} markets`;

  const fieldClass = disabled
    ? 'cursor-not-allowed border-background-200 bg-background-100 opacity-80'
    : 'cursor-text border-background-300 bg-background-50 focus-within:border-primary-400';

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {compact ? (
        <div
          className={`flex h-9 items-center gap-2 rounded-md border px-3 ${fieldClass}`}
          onClick={() => {
            if (!disabled) setOpen(true);
          }}
        >
          <i className="ri-map-pin-line text-foreground-400" />
          <input
            id={id}
            type="text"
            disabled={disabled}
            value={query}
            onFocus={() => {
              if (!disabled) setOpen(true);
            }}
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
            }}
            placeholder={summary}
            className="h-full w-full min-w-0 bg-transparent text-sm font-label text-foreground-700 placeholder:text-foreground-600 focus:outline-none disabled:cursor-not-allowed"
          />
          {value.length > 0 ? (
            <span className="shrink-0 rounded-full bg-primary-100 px-1.5 py-0.5 text-[10px] font-medium text-primary-800">
              {allSelected ? 'ALL' : value.length}
            </span>
          ) : null}
          <i
            className={`ri-arrow-down-s-line shrink-0 text-foreground-500 transition-transform ${
              open ? 'rotate-180' : ''
            }`}
          />
        </div>
      ) : (
        <div
          className={`flex min-h-[42px] w-full flex-wrap items-center gap-1.5 rounded-md border px-2 py-1.5 ${fieldClass}`}
          onClick={() => {
            if (!disabled) setOpen(true);
          }}
        >
          {value.length === 0 ? null : allSelected ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-primary-200 bg-primary-50 px-2 py-0.5 text-xs font-label text-primary-800">
              {ALL_AFRICAN_MARKETS}
              <button
                type="button"
                disabled={disabled}
                onClick={(event) => {
                  event.stopPropagation();
                  toggleAll();
                }}
                className="cursor-pointer text-primary-600 hover:text-primary-900 disabled:cursor-not-allowed"
                aria-label={`Remove ${ALL_AFRICAN_MARKETS}`}
              >
                <i className="ri-close-line" />
              </button>
            </span>
          ) : (
            value.map((name) => (
              <span
                key={name}
                className="inline-flex items-center gap-1 rounded-full border border-primary-200 bg-primary-50 px-2 py-0.5 text-xs font-label text-primary-800"
              >
                {name}
                <button
                  type="button"
                  disabled={disabled}
                  onClick={(event) => {
                    event.stopPropagation();
                    toggle(name);
                  }}
                  className="cursor-pointer text-primary-600 hover:text-primary-900 disabled:cursor-not-allowed"
                  aria-label={`Remove ${name}`}
                >
                  <i className="ri-close-line" />
                </button>
              </span>
            ))
          )}
          <input
            id={id}
            type="text"
            disabled={disabled}
            value={query}
            onFocus={() => {
              if (!disabled) setOpen(true);
            }}
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
            }}
            placeholder={value.length === 0 ? placeholder : 'Add another…'}
            className="h-7 min-w-[120px] flex-1 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none disabled:cursor-not-allowed"
          />
        </div>
      )}

      {open && !disabled ? (
        <div className="absolute z-30 mt-1 w-full min-w-[240px] overflow-hidden rounded-md border border-background-300 bg-background-50">
          <div className="flex items-center gap-2 border-b border-background-200 px-3 py-2">
            <i className="ri-search-line shrink-0 text-sm text-foreground-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search markets"
              className="h-6 w-full min-w-0 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
            />
            {query ? (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="shrink-0 cursor-pointer text-foreground-400 hover:text-foreground-700"
                aria-label="Clear search"
              >
                <i className="ri-close-line" />
              </button>
            ) : null}
          </div>

          <div className="max-h-56 overflow-y-auto">
            <button
              type="button"
              onClick={toggleAll}
              className="flex w-full cursor-pointer items-center gap-2 border-b border-background-200 px-3 py-2 text-left text-sm text-foreground-900 hover:bg-background-100"
            >
              <CheckBox checked={allSelected} />
              <span className="flex-1 font-medium">All</span>
              <span className="text-[11px] text-foreground-400">{africanMarkets.length} countries</span>
            </button>

            {results.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-foreground-400">
                No countries match “{query}”.
              </p>
            ) : (
              results.map((market) => {
                const checked = value.includes(market.name);
                return (
                  <button
                    key={market.name}
                    type="button"
                    onClick={() => toggle(market.name)}
                    className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm hover:bg-background-100"
                  >
                    <CheckBox checked={checked} />
                    <span className="flex-1 text-foreground-800">{market.name}</span>
                    <span className="text-[11px] font-label text-foreground-400">
                      {market.currency}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}